import { useEffect, useMemo } from 'react';
import { SITE_URL } from '../../utils/seo';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: object | object[];
}

function setMeta(selector: string, attrName: string, attrValue: string, content: string): void {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta') as HTMLMetaElement;
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(selector: string, rel: string, href: string): void {
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link') as HTMLLinkElement;
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogType = 'website',
  ogImage,
  jsonLd,
}: SEOHeadProps) {
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const jsonLdString = useMemo(() => (jsonLd !== undefined ? JSON.stringify(jsonLd) : ''), [jsonLd]);

  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    if (keywords) setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    setMeta('meta[name="last-modified"]', 'name', 'last-modified', new Date().toISOString().split('T')[0]);
    setLink('link[rel="canonical"]', 'canonical', canonicalUrl);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'VergeIO');
    if (ogImage) setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    if (ogImage) setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);
  }, [title, description, keywords, canonicalUrl, ogType, ogImage]);

  useEffect(() => {
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (!jsonLdString) return;
    const parsed = JSON.parse(jsonLdString) as object | object[];
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    arr.forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [jsonLdString]);

  return null;
}
